import { DOMAIN_EVENT_KEYS_BY_PROCESS, domainEventSourceProcess } from '@app/pages/admin/orchestration-process-events.util';

/** Class that implements INotificationTemplateModel.CreateModelAsync for an event. */
export const NOTIFICATION_TEMPLATE_MODEL_BY_EVENT: Readonly<Record<string, { namespace: string; className: string; method: string }>> = {
  'assignment.failed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'AssignmentFailedNotificationTemplateModel',
    method: 'CreateModelAsync',
  },
};

/** Parameters present on every process assignment notification. */
export const COMMON_NOTIFICATION_TEMPLATE_FIELDS = [
  'assignmentId',
  'processDefinitionKey',
  'processDefinitionVersion',
  'priority',
  'eventKey',
  'resultCount',
] as const;

/** Parameters added only for one process. Lists expand to their row fields. */
export const PROCESS_NOTIFICATION_TEMPLATE_FIELDS: Readonly<Record<string, readonly string[]>> = {
  'satellite.download-found-products': [
    'satelliteTaskId', 'downloadedProductCount', 'failedProductCount', 'downloadedSizeInBytes', 'estimatedSizeInBytes',
    'assignmentUrl', 'startedAt', 'completedAt', 'ranFor', 'taskError', 'totalDownloads', 'successfulDownloads',
    'failedDownloads', 'cancelledDownloads', 'productRows', 'products',
  ],
  'satellite.calculate-season-indices': [
    'assignmentUrl', 'startedAt', 'completedAt', 'ranFor', 'mode', 'fieldId', 'dateFrom', 'dateTo',
    'rowsWritten', 'productsProcessed', 'skippedSummary', 'runSummary', 'stepState', 'stepError', 'skips',
  ],
  'satellite.find-products': ['satelliteTaskId', 'productCount', 'estimatedSizeInBytes'],
  'crop.export-bid-templates': ['fileStorageItemId', 'exportedCount', 'ftpRemotePath', 'newBidCount'],
  'ai.calculate-anomalies': ['detectionYear'],
  'analitic.request-farmer-report-for-satellite-anomalies': [
    'candidateGroups', 'reportsCreated', 'errorCount', 'stepState', 'stepError', 'recentLogs', 'groups',
  ],
  'analitic.fetch-qwen-results': [
    'successCount', 'errorCount', 'stepState', 'stepError', 'recentLogs', 'errors',
  ],
  'analitic.request-farmer-report-for-finished-bids': [
    'fieldId', 'seasonId', 'reason', 'message',
    'candidatePairs', 'requestsSent', 'rejectedCandidates', 'errorCount', 'stepState', 'stepError', 'recentLogs', 'pairs',
  ],
  'weather.load-ugm-current': [
    'observations', 'processed', 'rowsSaved', 'bytesWritten', 'skipped', 'stationsCreated', 'regionsTouched', 'stepState', 'stepError', 'recentLogs', 'observationsList',
  ],
  'analitic.collect-qwen-full-analysis': [
    'checkedReports', 'savedReports', 'failedReports', 'partialReports', 'errorCount', 'stepState', 'stepError', 'recentLogs', 'errors',
  ],
  'analitic.submit-bids-to-qwen': [
    'candidateCount', 'successCount', 'errorCount', 'stepState', 'stepError', 'recentLogs', 'submissions', 'errors',
  ],
  'ai.transfer-yield-forecast-to-seasons': [
    'runId', 'mode', 'trigger', 'outcome', 'seasonsExamined', 'seasonsUpdated', 'seasonsRefreshed',
    'seasonsAdoptedUnstamped', 'seasonsNotReached', 'seasonsFailed', 'areaMismatchCount', 'stoppedOnBudget',
    'stepState', 'stepError', 'recentLogs', 'reasons',
  ],
  'orchestration.probe': ['ordinal', 'failEvery'],
};

export function processNotificationFieldNames(processKey: string): readonly string[] {
  const named = NOTIFICATION_TEMPLATE_MODEL_BY_NAME[`${processKey.trim().toLowerCase()}.completed`];
  const extra = named?.scalars ?? [];
  const listed = PROCESS_NOTIFICATION_TEMPLATE_FIELDS[processKey] ?? [];
  return [...new Set([...listed, ...extra])];
}

export function notificationTemplateParameterRows(names: readonly string[]): { token: string; descriptionKey: string }[] {
  const expanded = names.flatMap(name => [name, ...(NOTIFICATION_LIST_ITEM_FIELDS[name] ?? [])]);
  return [...new Set(expanded)].map(name => ({
    token: `{{${name}}}`,
    descriptionKey: NOTIFICATION_TEMPLATE_FIELD_DESCRIPTION_KEYS[name] ?? 'notificationField.unknown',
  }));
}

/** Template name from DescribeBaseTemplate when the event and process pick exactly one model. */
export function notificationBaseTemplateName(eventKey: string, processKey: string): string | null {
  const event = eventKey.trim().toLowerCase();
  const process = processKey.trim().toLowerCase();
  if (event === 'assignment.failed') {
    return 'assignment.failed';
  }
  const completed = `${process}.completed`;
  return event === 'assignment.completed' && NOTIFICATION_TEMPLATE_MODEL_BY_NAME[completed]
    ? completed
    : null;
}

/** True when this process has a DescribeBaseTemplate for assignment.completed. */
export function processHasCompletionTemplate(processKey: string): boolean {
  return notificationBaseTemplateName('assignment.completed', processKey) !== null;
}

/** Model behind a named base template from DescribeBaseTemplate. */
export const NOTIFICATION_TEMPLATE_MODEL_BY_NAME: Readonly<Record<string, { namespace: string; className: string; method: string; scalars: readonly string[]; lists: readonly string[] }>> = {
  'assignment.failed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'AssignmentFailedNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentId', 'processDefinitionKey', 'lastError', 'assignmentUrl', 'startedAt', 'failedAt', 'ranFor', 'stepsStarted', 'stepsCompleted', 'recentLogs', 'inputParameters'],
    lists: ['steps'],
  },
  'satellite.download-found-products.completed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'DownloadFoundProductsNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'ranFor', 'satelliteTaskId', 'taskError', 'totalDownloads', 'successfulDownloads', 'failedDownloads', 'cancelledDownloads', 'estimatedSizeInBytes', 'downloadedSizeInBytes', 'productRows'],
    lists: ['products'],
  },
  'satellite.calculate-season-indices.completed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'CalculateSeasonIndicesNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'ranFor', 'mode', 'fieldId', 'dateFrom', 'dateTo', 'resultCount', 'rowsWritten', 'productsProcessed', 'skippedSummary', 'runSummary', 'stepState', 'stepError'],
    lists: ['skips'],
  },
  'analitic.request-farmer-report-for-satellite-anomalies.completed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'RequestFarmerReportForSatelliteAnomaliesNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'candidateGroups', 'reportsCreated', 'errorCount', 'stepState', 'stepError', 'recentLogs'],
    lists: ['groups'],
  },
  'analitic.fetch-qwen-results.completed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'FetchQwenResultsNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'successCount', 'errorCount', 'stepState', 'stepError', 'recentLogs'],
    lists: ['errors'],
  },
  'ai.transfer-yield-forecast-to-seasons.completed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'TransferYieldForecastToSeasonsNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'runId', 'mode', 'trigger', 'outcome', 'seasonsExamined', 'seasonsUpdated', 'seasonsRefreshed', 'seasonsAdoptedUnstamped', 'seasonsSkippedBackwardsForecast', 'seasonsNotReached', 'seasonsUnchanged', 'seasonsSkippedNoForecast', 'seasonsSkippedIncompleteForecast', 'seasonsSkippedConcurrentEdit', 'seasonsFailed', 'valuesOverwritten', 'forecastsWithoutRegion', 'areaMismatchCount', 'stoppedOnBudget', 'stepState', 'stepError', 'recentLogs'],
    lists: ['reasons'],
  },
  'analitic.submit-bids-to-qwen.completed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'SubmitBidsToQwenNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'candidateCount', 'successCount', 'errorCount', 'stepState', 'stepError', 'recentLogs'],
    lists: ['submissions', 'errors'],
  },
  'analitic.collect-qwen-full-analysis.completed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'CollectQwenFullAnalysisNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'checkedReports', 'savedReports', 'failedReports', 'partialReports', 'errorCount', 'stepState', 'stepError', 'recentLogs'],
    lists: ['errors'],
  },
  'weather.load-ugm-current.completed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'LoadUgmCurrentWeatherNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'observations', 'processed', 'rowsSaved', 'bytesWritten', 'skipped', 'stationsCreated', 'regionsTouched', 'stepState', 'stepError', 'recentLogs'],
    lists: ['observationsList'],
  },
  'analitic.request-farmer-report-for-finished-bids.completed': {
    namespace: 'Masofa.BusinessLogic.UserNotifications',
    className: 'RequestFarmerReportForFinishedBidsNotificationTemplateModel',
    method: 'DescribeBaseTemplate',
    scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'candidatePairs', 'requestsSent', 'rejectedCandidates', 'errorCount', 'stepState', 'stepError', 'recentLogs'],
    lists: ['pairs'],
  },
  'satellite.find-products.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'FindSatelliteProductsNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'newProductCount', 'productCount', 'productsWithKnownSize', 'estimatedSizeInBytes'], lists: [] },
  'satellite.create-product-postprocessing.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CreateSatelliteProductPostProcessingNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'satellite.catch-up-product-postprocessing.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CatchUpSatelliteProductPostProcessingNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'started', 'failed'], lists: [] },
  'satellite.country-rgb-layers.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CountryRgbLayersNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'processed'], lists: [] },
  'satellite.index-tiff-ingest.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'IndexTiffIngestNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'processed'], lists: [] },
  'satellite.refresh-copernicus-metadata.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'RefreshCopernicusMetadataNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'updated'], lists: [] },
  'satellite.country-rgb-mosaic.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CountryRgbMosaicNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'yearsProcessed'], lists: [] },
  'satellite.index-tile-map-backfill.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'IndexTileMapBackfillNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'dateCount'], lists: [] },
  'satellite.calculate-index-layers.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CalculateIndexLayersNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'layersPublished'], lists: [] },
  'satellite.backfill-season-indices.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'BackfillSeasonIndicesNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'rowsWritten'], lists: [] },
  'weather.load-era-raw.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'LoadEraWeatherRawNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'processedDays'], lists: [] },
  'weather.calculate-era-reports.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CalculateEraWeatherReportsNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'dayReportsProcessed'], lists: [] },
  'weather.generate-era-tiles.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'GenerateEraWeatherTilesNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'created'], lists: [] },
  'weather.maintain-era-raw.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'MaintainEraWeatherRawNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'weather.reconcile-era-reports.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'ReconcileEraWeatherReportsNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'finalDaysProcessed', 'era5TDaysProcessed'], lists: [] },
  'weather.refresh-era-report-tiles.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'RefreshEraWeatherReportTilesNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'created'], lists: [] },
  'weather.fill-era-sat.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'FillEraSatNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'dayReportsProcessed'], lists: [] },
  'weather.update-era-normalized.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'UpdateEraNormalizedWeatherNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'weather.generate-era-deviation-tiles.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'GenerateEraDeviationTilesNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'created'], lists: [] },
  'weather.generate-era-yearly-atd-tiles.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'GenerateEraYearlyAtdTilesNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'processed'], lists: [] },
  'weather.fill-era-deviation-2020.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'FillEraDeviation2020NotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'processed'], lists: [] },
  'weather.load-ibm-historical.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'LoadIbmWeatherHistoricalNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'processedDays', 'rowsSaved', 'bytesWritten'], lists: [] },
  'weather.load-ugm-forecast.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'LoadUgmForecastWeatherNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'processed', 'rowsSaved', 'bytesWritten'], lists: [] },
  'geometry.resolve.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'ResolveGeometryNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'ai.identify-crops.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'IdentifyCropsNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'ai.calculate-yield-forecast.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CalculateYieldForecastNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'ai.start-segmentation.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'StartSegmentationNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'ai.calculate-anomalies.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CalculateAnomaliesNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'indices.detect-recurring-anomalies.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'DetectRecurringAnomaliesNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'clustersWritten'], lists: [] },
  'analitic.generate-farmer-reports.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'GenerateAnaliticReportsNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'successCount'], lists: [] },
  'crop.export-bid-templates.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'ExportBidTemplatesNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'newBidCount', 'exportedCount', 'fileStorageItemId', 'ftpRemotePath'], lists: [] },
  'satellite.repair-products.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'RepairSatelliteProductsNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'repaired'], lists: [] },
  'crop.sync-cadastre-parcels.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'SyncCadastreParcelsNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'totalFetched'], lists: [] },
  'ai.run-crop-classification.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'RunCropClassificationNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'polygonCount'], lists: [] },
  'analitic.collect-plant-diag-results.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CollectPlantDiagResultsNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'savedReports'], lists: [] },
  'orchestration.probe.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'OrchestrationProbeNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs', 'failEvery', 'ordinal'], lists: [] },
  'crop.apply-crop-agro-lands.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'ApplyCropAgroLandsNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'weather.check-ibm-day-data.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CheckIbmDayDataNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'weather.check-era5-day-data.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'CheckEra5DayDataNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
  'ai.classify-crops-onnx.completed': { namespace: 'Masofa.BusinessLogic.UserNotifications', className: 'ClassifyCropsOnnxNotificationTemplateModel', method: 'DescribeBaseTemplate', scalars: ['assignmentUrl', 'startedAt', 'completedAt', 'resultCount', 'stepState', 'stepError', 'recentLogs'], lists: [] },
};

/** Which event and process a library template name belongs to. Unknown names have no binding. */
export function notificationTemplateBinding(name: string): { eventKey: string; processKey: string } | null {
  const key = name.trim().toLowerCase();
  if (!key) {
    return null;
  }
  const base = key.split(' ')[0];
  if (base === 'assignment.failed') {
    return { eventKey: 'assignment.failed', processKey: '' };
  }
  if (base.endsWith('.completed') && NOTIFICATION_TEMPLATE_MODEL_BY_NAME[base]) {
    return { eventKey: 'assignment.completed', processKey: base.slice(0, -'.completed'.length) };
  }
  const eventKey = Object.values(DOMAIN_EVENT_KEYS_BY_PROCESS)
    .flat()
    .find(event => base === event);
  if (eventKey) {
    return { eventKey, processKey: domainEventSourceProcess(eventKey)?.processKey ?? '' };
  }
  if (base === 'orchestration.probe.failed') {
    return { eventKey: 'assignment.failed', processKey: 'orchestration.probe' };
  }
  return null;
}

/** Meaning of each placeholder written by INotificationTemplateModel, keyed for i18n. */
export const NOTIFICATION_TEMPLATE_FIELD_DESCRIPTION_KEYS: Readonly<Record<string, string>> = {
  assignmentId: 'notificationField.assignmentId',
  processDefinitionKey: 'notificationField.processDefinitionKey',
  lastError: 'notificationField.lastError',
  assignmentUrl: 'notificationField.assignmentUrl',
  startedAt: 'notificationField.startedAt',
  failedAt: 'notificationField.failedAt',
  ranFor: 'notificationField.ranFor',
  stepsStarted: 'notificationField.stepsStarted',
  stepsCompleted: 'notificationField.stepsCompleted',
  recentLogs: 'notificationField.recentLogs',
  inputParameters: 'notificationField.inputParameters',
  steps: 'notificationField.steps',
  stepKey: 'notificationField.stepKey',
  state: 'notificationField.state',
  attempt: 'notificationField.attempt',
  fieldId: 'notificationField.fieldId',
  seasonId: 'notificationField.seasonId',
  reason: 'notificationField.reason',
  message: 'notificationField.message',
  newBidCount: 'notificationField.newBidCount',
  resultCount: 'notificationField.resultCount',
  fileStorageItemId: 'notificationField.fileStorageItemId',
  satelliteProductId: 'notificationField.satelliteProductId',
  productId: 'notificationField.productId',
  provider: 'notificationField.provider',
  downloadState: 'notificationField.downloadState',
  attemptCount: 'notificationField.attemptCount',
  completedAt: 'notificationField.completedAt',
  satelliteTaskId: 'notificationField.satelliteTaskId',
  taskError: 'notificationField.taskError',
  totalDownloads: 'notificationField.totalDownloads',
  successfulDownloads: 'notificationField.successfulDownloads',
  failedDownloads: 'notificationField.failedDownloads',
  cancelledDownloads: 'notificationField.cancelledDownloads',
  estimatedSizeInBytes: 'notificationField.estimatedSizeInBytes',
  downloadedSizeInBytes: 'notificationField.downloadedSizeInBytes',
  productRows: 'notificationField.productRows',
  products: 'notificationField.products',
  mode: 'notificationField.mode',
  dateFrom: 'notificationField.dateFrom',
  dateTo: 'notificationField.dateTo',
  rowsWritten: 'notificationField.rowsWritten',
  productsProcessed: 'notificationField.productsProcessed',
  skippedSummary: 'notificationField.skippedSummary',
  runSummary: 'notificationField.runSummary',
  stepState: 'notificationField.stepState',
  stepError: 'notificationField.stepError',
  skips: 'notificationField.skips',
  count: 'notificationField.count',
  eventKey: 'notificationField.eventKey',
  processDefinitionVersion: 'notificationField.processDefinitionVersion',
  priority: 'notificationField.priority',
  downloadedProductCount: 'notificationField.downloadedProductCount',
  failedProductCount: 'notificationField.failedProductCount',
  productCount: 'notificationField.productCount',
  exportedCount: 'notificationField.exportedCount',
  ftpRemotePath: 'notificationField.ftpRemotePath',
  detectionYear: 'notificationField.detectionYear',
  ordinal: 'notificationField.ordinal',
  failEvery: 'notificationField.failEvery',
  candidateGroups: 'notificationField.candidateGroups',
  reportsCreated: 'notificationField.reportsCreated',
  errorCount: 'notificationField.errorCount',
  detectionDate: 'notificationField.detectionDate',
  created: 'notificationField.created',
  reportCount: 'notificationField.reportCount',
  groups: 'notificationField.groups',
  successCount: 'notificationField.successCount',
  errors: 'notificationField.errors',
  runId: 'notificationField.runId',
  outcome: 'notificationField.outcome',
  seasonsExamined: 'notificationField.seasonsExamined',
  seasonsUpdated: 'notificationField.seasonsUpdated',
  seasonsRefreshed: 'notificationField.seasonsRefreshed',
  seasonsAdoptedUnstamped: 'notificationField.seasonsAdoptedUnstamped',
  seasonsNotReached: 'notificationField.seasonsNotReached',
  seasonsFailed: 'notificationField.seasonsFailed',
  areaMismatchCount: 'notificationField.areaMismatchCount',
  stoppedOnBudget: 'notificationField.stoppedOnBudget',
  reasons: 'notificationField.reasons',
  candidateCount: 'notificationField.candidateCount',
  qwenJobId: 'notificationField.qwenJobId',
  submissions: 'notificationField.submissions',
  bidId: 'notificationField.bidId',
  checkedReports: 'notificationField.checkedReports',
  savedReports: 'notificationField.savedReports',
  failedReports: 'notificationField.failedReports',
  partialReports: 'notificationField.partialReports',
  observations: 'notificationField.observations',
  processed: 'notificationField.processed',
  rowsSaved: 'notificationField.rowsSaved',
  bytesWritten: 'notificationField.bytesWritten',
  skipped: 'notificationField.skipped',
  stationsCreated: 'notificationField.stationsCreated',
  regionsTouched: 'notificationField.regionsTouched',
  observationsList: 'notificationField.observationsList',
  regionId: 'notificationField.regionId',
  date: 'notificationField.date',
  dayPart: 'notificationField.dayPart',
  weatherCode: 'notificationField.weatherCode',
  candidatePairs: 'notificationField.candidatePairs',
  requestsSent: 'notificationField.requestsSent',
  rejectedCandidates: 'notificationField.rejectedCandidates',
  pairs: 'notificationField.pairs',
  reportIds: 'notificationField.reportIds',
};

/** Row properties of a list field in the notification template model. */
export const NOTIFICATION_LIST_ITEM_FIELDS: Readonly<Record<string, readonly string[]>> = {
  steps: ['stepKey', 'state', 'attempt', 'startedAt'],
  products: ['satelliteProductId', 'productId', 'provider', 'downloadState', 'attemptCount', 'lastError', 'completedAt'],
  skips: ['reason', 'count'],
  groups: ['fieldId', 'seasonId', 'detectionDate', 'created', 'reportCount', 'error'],
  errors: ['message'],
  reasons: ['reason', 'count'],
  submissions: ['bidId', 'qwenJobId'],
  observationsList: ['regionId', 'date', 'dayPart', 'weatherCode'],
  pairs: ['fieldId', 'seasonId', 'created', 'reportIds', 'reason', 'error'],
};

export interface NotificationTemplateListFields {
  name: string;
  tokens: string[];
}

export interface NotificationTemplateRef {
  id: string;
  name: string;
  lines: { label: string; text: string }[];
}

export interface NotificationTemplateFieldEvent {
  key: string;
  displayName: string;
  tokens: string[];
  lists: NotificationTemplateListFields[];
  templates: NotificationTemplateRef[];
}

export function notificationTemplateFieldEvents(
  events: ReadonlyArray<{ key: string; displayName?: string; payloadFields?: readonly string[] }>,
  onlyKeys?: readonly string[],
  keepWithoutFields = false,
): NotificationTemplateFieldEvent[] {
  const allowed = onlyKeys?.map(key => key.trim().toLowerCase()).filter(Boolean);
  const views: NotificationTemplateFieldEvent[] = [];
  for (const event of events) {
    const key = event.key?.trim();
    if (!key) {
      continue;
    }
    if (allowed && !allowed.includes(key.toLowerCase())) {
      continue;
    }
    const fields = (event.payloadFields ?? []).map(field => field.trim()).filter(Boolean);
    if (fields.length === 0 && !keepWithoutFields) {
      continue;
    }
    views.push({
      key,
      displayName: event.displayName?.trim() || key,
      tokens: fields.map(field => `{{${field}}}`),
      lists: fields
        .filter(field => NOTIFICATION_LIST_ITEM_FIELDS[field])
        .map(field => ({
          name: field,
          tokens: NOTIFICATION_LIST_ITEM_FIELDS[field].map(item => `{{${item}}}`),
        })),
      templates: [],
    });
  }
  return views;
}

/** Templates from rules that target these events for every process or for `processKey`. */
export function notificationTemplatesForEvents(
  events: NotificationTemplateFieldEvent[],
  rules: ReadonlyArray<{ eventKey: string; sourceProcessDefinitionKey?: string | null; templateId: string }>,
  templates: ReadonlyArray<{
    id: string;
    name: string;
    title?: Record<string, string>;
    body?: Record<string, string>;
  }>,
  processKey: string,
): NotificationTemplateFieldEvent[] {
  const process = processKey.trim().toLowerCase();
  const byId = new Map(templates.map(template => [template.id, template]));
  return events.map(event => {
    const seen = new Set<string>();
    const matched: NotificationTemplateRef[] = [];
    for (const rule of rules) {
      if (rule.eventKey.trim().toLowerCase() !== event.key.toLowerCase()) {
        continue;
      }
      const source = rule.sourceProcessDefinitionKey?.trim().toLowerCase() ?? '';
      if (source && source !== process) {
        continue;
      }
      const id = rule.templateId?.trim();
      if (!id || seen.has(id)) {
        continue;
      }
      seen.add(id);
      const template = byId.get(id);
      matched.push({
        id,
        name: template?.name?.trim() || id,
        lines: templateLines(template),
      });
    }
    return { ...event, templates: matched };
  });
}

function templateLines(template?: { title?: Record<string, string>; body?: Record<string, string> }): { label: string; text: string }[] {
  const title = template?.title ?? {};
  const body = template?.body ?? {};
  return [
    { label: 'en-US', text: [title['en-US'], body['en-US']].filter(Boolean).join('\n') },
    { label: 'ru-RU', text: [title['ru-RU'], body['ru-RU']].filter(Boolean).join('\n') },
    { label: 'uz-Latn-UZ', text: [title['uz-Latn-UZ'], body['uz-Latn-UZ']].filter(Boolean).join('\n') },
  ].filter(line => line.text);
}
